import {
  DataTable,
  DataTableColumn,
  DataTableOptions,
  DataType,
  FilterType,
  UserPermission
} from '@globaleris/ui.library'
import { Delete, Edit } from '@material-ui/icons'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { StandardCard } from '../../../../../components/shared/standardCard/standardCard'
import { getUserPermissions } from '../../../../../helpers/permissionsHelper'
import { Pages } from '../../../../../model/RBAC/constants'
import { getCustomerDomain } from '../../../../../redux'
import { QuotesProductTableModel } from '../../../quotes.model'

interface QuotesProductsCardProps {
  data: QuotesProductTableModel[]
  isLoading?: boolean
  subtitle?: string
  userPermissions?: UserPermission[]
  emptyButtonCallback?: () => void
  onAddQuoteContractClick?: () => Promise<void>
  onEditQuoteContractClick?: (selectedRow: QuotesProductTableModel) => void
  onDeleteQuoteContractClick?: (selectedRow: QuotesProductTableModel) => void
}

export const QuotesProductsCard: React.FC<QuotesProductsCardProps> = ({
  data,
  isLoading,
  subtitle,
  userPermissions,
  emptyButtonCallback,
  onAddQuoteContractClick,
  onEditQuoteContractClick,
  onDeleteQuoteContractClick
}) => {
  const [t] = useTranslation(['globalerisLibrary', 'enums', 'quotes'])

  const noData = data && data.length === 0

  // #region Handlers Starts
  const handleInsertAction = async () => {
    onAddQuoteContractClick && onAddQuoteContractClick()
    return true
  }

  const handleEditAction = async (selectedRow: QuotesProductTableModel) => {
    onEditQuoteContractClick && onEditQuoteContractClick(selectedRow)
  }

  const handleDeleteAction = async (selectedRow: QuotesProductTableModel) => {
    onDeleteQuoteContractClick && onDeleteQuoteContractClick(selectedRow)
  }

  // #endregion Handlers Ends

  // #region Functions Starts
  // #endregion Functions Ends

  // #region Form Definition Starts
  // #endregion Form Definition Ends

  // #region DataTable Config Starts

  const quotesProductDataTableOptions: DataTableOptions = {
    i18n: t('globalerisLibrary:dataTable', { returnObjects: true }),
    search: true,
    elevation: 0,
    filterType: FilterType.ADVANCED,
    download: true,
    canEdit: false,
    canDelete: false,
    customInsertEditModal: true,
    pagination: true,

    analytics: {
      customer: getCustomerDomain(),
      view: t('quotes:components.quotesProductsCard.dataTable.analytics')
    },
    onRowInsert: handleInsertAction,
    rowCustomActions: [
      {
        icon: <Edit fontSize="small" />,
        tooltip: t('quotes:components.quotesProductsCard.dataTable.tooltips.edit'),
        onClick: handleEditAction,
        userPermission: UserPermission.EDIT
      },
      {
        icon: <Delete fontSize="small" />,
        tooltip: t('quotes:components.quotesProductsCard.dataTable.tooltips.delete'),
        onClick: handleDeleteAction,
        userPermission: UserPermission.EDIT
      }
    ]
  }

  const quotesProductDataTableColumns: DataTableColumn[] = [
    {
      name: 'quoteId',
      display: false
    },
    {
      name: 'contractId',
      display: false
    },
    {
      name: 'code',
      label: t('quotes:components.quotesProductsCard.dataTable.columns.code'),
      type: DataType.TEXT
    },
    {
      name: 'description',
      label: t('quotes:components.quotesProductsCard.dataTable.columns.description'),
      type: DataType.TEXT
    },
    {
      name: 'signDate',
      label: t('quotes:components.quotesProductsCard.dataTable.columns.signDate'),
      type: DataType.DATE
    },
    {
      name: 'startDate',
      label: t('quotes:components.quotesProductsCard.dataTable.columns.startDate'),
      type: DataType.DATE
    },
    {
      name: 'endDate',
      label: t('quotes:components.quotesProductsCard.dataTable.columns.endDate'),
      type: DataType.DATE
    }
  ]

  // #endregion DataTable Config Ends

  // #region Hooks Starts
  // #endregion Hooks Ends

  // #region Footer Actions Starts
  // #endregion Footer Actions Ends
  return (
    <StandardCard
      title={t('quotes:components.quotesProductsCard.title')}
      titleVariant={'secondary'}
      subtitle={subtitle}
      isLoading={isLoading}
      showEmptyContent={noData}
      emptyCaption={t('quotes:components.quotesProductsCard.emptyCaption')}
      emptyButtonCallback={emptyButtonCallback}
      emptyButtonCaption={t('quotes:components.quotesProductsCard.mainAction')}
    >
      <DataTable
        data={data}
        columns={quotesProductDataTableColumns}
        options={quotesProductDataTableOptions}
        userPermissions={
          userPermissions ? userPermissions : getUserPermissions(Pages.QuotesProducts)
        }
      />
    </StandardCard>
  )
}
